import {
  amber, grey, green, red, common,
} from '@mui/material/colors';

// Quadrant brand colors, sampled from the official logo mark.
//
// The palette is a function of the mode because a colour that works on paper-white does not work on
// near-black: the brand navy is almost invisible against a dark ground, and the semantic reds and
// ambers need lifting or they read as brown. So each mode gets its own version of the same
// identity rather than one set of values used twice.
const brand = {
  navy: '#123247',
  teal: '#008C95',
  geometry: '#3bb2d0',
};

const light = {
  background: '#F4F6F9',
  surface: common.white,
  primary: brand.navy,
  secondary: '#046F76',
  // Teal at brand strength fails contrast as small text on white, so interactive text gets a
  // darkened version of the same hue rather than a different colour.
  interactive: '#046f76',
  positive: green[700],
  medium: amber[800],
  negative: red[700],
  neutral: grey[600],
  line: 'rgba(18, 50, 71, 0.12)',
  textPrimary: '#10212D',
  textSecondary: '#53687A',
  // The cool family the product is coloured with. Each value is dark enough to pass AA as small
  // text on a pale tint of itself, because these are used as label colours and not only as fills.
  accents: {
    slate: '#44607A',
    blue: '#1B5E9E',
    teal: '#046F76',
    indigo: '#3F4F9E',
    violet: '#6B3FA0',
    neutral: '#53687A',
  },
};

const dark = {
  // Neutrals carry a little of the brand navy rather than being flat grey, so the dark mode reads
  // as the same product with the lights off.
  background: '#0E1620',
  surface: '#16202B',
  primary: '#7FB2CB',
  secondary: '#2BB8C0',
  interactive: '#45C8D0',
  positive: green[400],
  medium: amber[400],
  negative: red[400],
  neutral: grey[400],
  line: 'rgba(255, 255, 255, 0.14)',
  textPrimary: '#E6EDF3',
  textSecondary: '#9FB0BF',
  // The same five hues lifted for a dark ground. Kept in the same order so a stage that is blue in
  // daylight is blue at night.
  accents: {
    slate: '#9BB3C7',
    blue: '#6FB2E8',
    teal: '#45C8D0',
    indigo: '#9BA8F0',
    violet: '#C3A0F0',
    neutral: '#9FB0BF',
  },
};

export default (mode = 'light') => {
  const tone = mode === 'dark' ? dark : light;
  return {
    mode,
    background: {
      default: tone.background,
      paper: tone.surface,
    },
    primary: {
      main: tone.primary,
      contrastText: mode === 'dark' ? '#0E1620' : common.white,
    },
    secondary: {
      main: tone.secondary,
      contrastText: mode === 'dark' ? '#0E1620' : '#ffffff',
    },
    text: {
      primary: tone.textPrimary,
      secondary: tone.textSecondary,
    },
    divider: tone.line,
    success: { main: tone.positive },
    warning: { main: tone.medium },
    error: { main: tone.negative },
    // Kept for the screens that read colours by name rather than by role. Every value moves with
    // the mode, so nothing that uses these needs to know which mode it is in.
    colors: {
      white: common.white,
      background: tone.background,
      surface: tone.surface,
      primary: tone.primary,
      secondary: tone.secondary,
      interactive: tone.interactive,
      positive: tone.positive,
      medium: tone.medium,
      negative: tone.negative,
      neutral: tone.neutral,
      line: tone.line,
      geometry: brand.geometry,
    },
    accents: tone.accents,
  };
};
